import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { message } from 'antd';
import {
  Users,
  BarChart3,
  ClipboardList,
  LogOut,
  User,
  FileText,
  Barcode,
  Archive,
  Upload,
  CheckSquare,
  Files,
  CheckCircle,
  DockIcon,
  ShipWheel,
  ChevronDown,
  ChevronUp,
  Calendar,
  Ship,
  BookOpen,
  Flame,
  MessageSquare,
  Ticket,
  Plane,
  Anchor,
  CreditCard,
} from 'lucide-react';
import { colors, spacing, radius, typography, shadows, transitions } from '../../styles/monochromeTheme';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const [isCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    propositions: true,
    suivi: false,
    contrats: true,
    equipe: false,
    outils: false,
    fermeture: true,
    taches: true
  });
  const [hoveredItem, setHoveredItem] = useState(null);

  // Save scroll position when it changes
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const handleScroll = () => {
      scrollPositionRef.current = sidebar.scrollTop;
    };

    sidebar.addEventListener('scroll', handleScroll);
    return () => sidebar.removeEventListener('scroll', handleScroll);
  }, []);

  // Restore scroll position after route change
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (sidebar && scrollPositionRef.current) {
      sidebar.scrollTop = scrollPositionRef.current;
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      message.success('Deconnexion reussie');
      navigate('/login');
    } catch (error) {
      message.error('Erreur lors de la deconnexion: ' + error.message);
    }
  };

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const styles = {
    sidebar: {
      width: isCollapsed ? '70px' : '260px',
      height: '100vh',
      background: '#111111',
      color: colors.textInverse,
      padding: isCollapsed ? '16px 8px' : '20px 10px',
      overflowY: 'auto',
      overflowX: 'visible',
      flexShrink: 0,
      transition: transitions.slow,
      position: 'relative',
      borderRight: '1px solid #1e1e1e',
      display: 'flex',
      flexDirection: 'column',
    },
    sidebarSection: {
      marginBottom: spacing.sm,
    },
    sidebarSectionTitle: {
      fontSize: isCollapsed ? '0px' : typography.fontSize.xs,
      height: isCollapsed ? '0px' : 'auto',
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.35)',
      marginBottom: isCollapsed ? spacing.xs : '6px',
      padding: isCollapsed ? '0px' : `6px ${spacing.md}`,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      transition: transitions.normal,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRadius: radius.md,
      userSelect: 'none',
      fontWeight: 700,
      letterSpacing: '0.08em',
    },
    sectionContent: {
      overflow: 'hidden',
      transition: transitions.slow,
    },
    sidebarItem: {
      display: 'flex',
      alignItems: 'center',
      padding: isCollapsed ? '12px' : '9px 14px',
      textDecoration: 'none',
      color: 'rgba(255,255,255,0.62)',
      borderRadius: 10,
      transition: transitions.normal,
      marginBottom: 2,
      marginLeft: 4,
      marginRight: 4,
      cursor: 'pointer',
      justifyContent: isCollapsed ? 'center' : 'flex-start',
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      position: 'relative',
      background: 'transparent',
      border: '1px solid transparent',
    },
    activeItem: {
      background: 'rgba(255,255,255,0.12)',
      fontWeight: typography.fontWeight.semibold,
      color: '#ffffff',
      borderLeft: '2px solid #ffffff',
      borderTop: '1px solid transparent',
      borderRight: '1px solid transparent',
      borderBottom: '1px solid transparent',
      paddingLeft: '14px',
    },
    icon: {
      marginRight: isCollapsed ? '0px' : spacing.md,
      flexShrink: 0,
    },
    itemLabel: {
      display: isCollapsed ? 'none' : 'block',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      fontSize: typography.fontSize.sm,
    },
    logoutButton: {
      background: 'rgba(220,38,38,0.1)',
      color: '#ff6b6b',
      border: '1px solid rgba(220,38,38,0.2)',
      fontWeight: typography.fontWeight.semibold,
    }
  };

  const SidebarItem = ({ to, label, icon: Icon }) => {
    const isHovered = hoveredItem === to;

    return (
      <NavLink
        to={to}
        style={({ isActive }) => ({
          ...styles.sidebarItem,
          ...(isActive ? styles.activeItem : {}),
          ...(isHovered && !isActive ? {
            background: 'rgba(255,255,255,0.08)',
            transform: 'translateX(3px)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.9)',
          } : {}),
        })}
        title={isCollapsed ? label : ''}
        onMouseEnter={() => setHoveredItem(to)}
        onMouseLeave={() => setHoveredItem(null)}
      >
        <Icon style={styles.icon} size={18} strokeWidth={2.2} />
        <span style={styles.itemLabel}>{label}</span>
      </NavLink>
    );
  };

  const CollapsibleSection = ({ title, sectionKey, children, icon: SectionIcon, alwaysOpen = false }) => {
    const isExpanded = alwaysOpen || expandedSections[sectionKey];
    const [isHovered, setIsHovered] = useState(false);

    return (
      <div style={styles.sidebarSection}>
        <h2
          style={{
            ...styles.sidebarSectionTitle,
            ...(isHovered && !isCollapsed && !alwaysOpen ? {
              background: 'rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.8)',
            } : {}),
            ...(alwaysOpen ? { cursor: 'default' } : {})
          }}
          onClick={() => !isCollapsed && !alwaysOpen && toggleSection(sectionKey)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
            {SectionIcon && <SectionIcon size={13} strokeWidth={2.5} />}
            {title}
          </span>
          {!isCollapsed && !alwaysOpen && (
            isExpanded ? <ChevronUp size={13} strokeWidth={3} /> : <ChevronDown size={13} strokeWidth={3} />
          )}
        </h2>
        <div style={{
          ...styles.sectionContent,
          maxHeight: isExpanded ? '600px' : '0px',
          opacity: isExpanded ? '1' : '0'
        }}>
          {children}
        </div>
      </div>
    );
  };

  return (
    <div ref={sidebarRef} className="masterski-sidebar" style={styles.sidebar}>

      <nav style={{ flex: 1 }}>
        <CollapsibleSection title="Propositions" sectionKey="propositions" icon={ClipboardList}>
          {/* <SidebarItem to="/charter-pricing" label="Charters" icon={Anchor} /> */}
          <SidebarItem to="/shortcuts" label="Raccourcis" icon={ClipboardList} />
          <SidebarItem to="/quick-replies" label="Réponses Rapides" icon={MessageSquare} />
        </CollapsibleSection>

        <CollapsibleSection title="Contrats" sectionKey="contrats" icon={FileText}>
          <SidebarItem to="/contracts" label="Générer" icon={FileText} />
          <SidebarItem to="/document-list" label="Liste" icon={Files} />
          <SidebarItem to="/payment-tracking" label="Paiements" icon={CreditCard} />
        </CollapsibleSection>

        <CollapsibleSection title="Tâches" sectionKey="fermeture" icon={CheckSquare}>
          <SidebarItem to="/sur-le-feu" label="Sur le Feu" icon={Flame} />
          <SidebarItem to="/transfer-tracking" label="Transferts" icon={Plane} />
          <SidebarItem to="/clairance-tracker" label="Clairances" icon={Anchor} />
          <SidebarItem to="/tickets" label="Tickets" icon={Ticket} />
          <SidebarItem to="/schedule-georges" label="Schedule Georges" icon={Ship} />
          <SidebarItem to="/closing-checklist" label="Checklist" icon={CheckSquare} />
          <SidebarItem to="/berth-documents" label="Places a Quai" icon={DockIcon} />
          <SidebarItem to="/planning-validator" label="Planning" icon={CheckCircle} />
          <SidebarItem to="/agenda-editor" label="Agenda du Jour" icon={Calendar} />
          <SidebarItem to="/off-days" label="Off et Astreintes" icon={Calendar} />
        </CollapsibleSection>

        <CollapsibleSection title="Équipe & Flotte" sectionKey="equipe" icon={ShipWheel}>
          <SidebarItem to="/employee-list" label="Équipe" icon={User} />
          <SidebarItem to="/passports" label="Passeports" icon={BookOpen} />
          <SidebarItem to="/yacht-management" label="Flotte" icon={ShipWheel} />
        </CollapsibleSection>

        <CollapsibleSection title="Suivi" sectionKey="suivi" icon={BarChart3}>
          <SidebarItem to="/cardex" label="Cardex" icon={Users} />
          <SidebarItem to="/charter-analytics" label="Graphiques" icon={BarChart3} />
        </CollapsibleSection>

        <CollapsibleSection title="Outils" sectionKey="outils" icon={Archive}>
          <SidebarItem to="/connaissances" label="Connaissances" icon={BookOpen} />
          <SidebarItem to="/compress-passports" label="Compresser" icon={Archive} />
          <SidebarItem to="/passport-analyzer" label="Décoder" icon={Barcode} />
          <SidebarItem to="/csv-uploader" label="Upload CSV" icon={Upload} />
        </CollapsibleSection>
      </nav>

      <div style={{
        marginTop: 'auto',
        paddingTop: spacing.lg,
        paddingBottom: spacing.md,
        borderTop: `1px solid rgba(255,255,255,0.1)`,
      }}>
        <div
          onClick={handleLogout}
          style={{
            ...styles.sidebarItem,
            ...styles.logoutButton,
            marginBottom: spacing.xs,
            ...(hoveredItem === 'logout' ? {
              transform: 'scale(1.02)',
              boxShadow: shadows.md,
              background: '#dc2626',
              color: colors.textInverse,
            } : {}),
          }}
          title="Deconnexion"
          onMouseEnter={() => setHoveredItem('logout')}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <LogOut style={styles.icon} size={18} strokeWidth={2.2} />
          <span style={styles.itemLabel}>Deconnexion</span>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
